// Board attention strip + filter toolbar. Presentational — all state lives in the board; this just
// renders the controls and calls back. Split out of board.tsx.
import { CarOutlined, ReloadOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, Select, Space, Switch, Tag, Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { LogisticsSummary } from "../types";
import { TeamMember, WarehouseOption } from "./logistics.api";
import { GROUP_BY_OPTIONS, PROVIDER_OPTIONS } from "./board_grouping";
import { ATTENTION_META, ATTENTION_ORDER } from "./components/board_meta";

// Sentinel value for the Owner filter's "no owner yet" option — distinct from "" (no filter) and
// from any real user id. The board maps it to the `unassigned` list/summary param.
export const UNASSIGNED_OWNER = "__unassigned__";

// The cities MD runs a desk in. A fixed list, not one derived from the board: the ops floor is
// split between two city teams, so the choices must be the same every day — including on a day one
// city's board happens to be empty. The server resolves each to its delivery-pincode belt (560/561/
// 562 vs 500/501/502) rather than matching the address's city text, which is often a locality.
export const CITY_OPTIONS = [
  { value: "Bangalore", label: "Bangalore" },
  { value: "Hyderabad", label: "Hyderabad" },
  // Not a city: customers no hub serves (off the hub pincode list, over 100 km from every hub) —
  // the same rows the "Outstation client" tag is on. The server resolves the value, not the label.
  { value: "outstation", label: "Outstation client" },
];

interface Props {
  summary: LogisticsSummary | null;
  attention: string;
  setAttention: (v: string) => void;
  sla: string;
  setSla: (v: string) => void;
  date: Dayjs;
  setDate: (v: Dayjs) => void;
  provider: string;
  setProvider: (v: string) => void;
  warehouse: number | undefined;
  setWarehouse: (v: number | undefined) => void;
  warehouses: WarehouseOption[];
  city: string | undefined;
  // Absent on a board whose city is fixed (the Outstation client tab) — the tab already says it,
  // so the dropdown is not shown.
  setCity?: (v: string | undefined) => void;
  assignee: string; // the selected owner's user id ("" = no filter)
  setAssignee: (v: string) => void;
  teamMembers: TeamMember[]; // the ops team, for the Owner filter dropdown
  expressOnly: boolean;
  setExpressOnly: (v: boolean) => void;
  transferOnly: boolean;
  setTransferOnly: (v: boolean) => void;
  showCancelled: boolean;
  setShowCancelled: (v: boolean) => void;
  groupBy: string;
  setGroupBy: (v: string) => void;
  // Vendor (pickup point) — FIRST MILE only, where a row's origin IS the supplier; on last mile the
  // origin is our own warehouse and the cut would say nothing. The setter's absence hides it.
  vendor?: string;
  setVendor?: (v: string | undefined) => void;
  vendorOptions?: string[];
  searchInput: string;
  setSearchInput: (v: string) => void;
  // Set on a search-only board, where the box IS the screen's purpose.
  autoFocusSearch?: boolean;
  filtersApplied: boolean;
  clearFilters: () => void;
  onRefresh: () => void;
  loading: boolean;
  onAssignTrip: () => void;
  canAssignTrip: boolean; // `oms.update` — the trip-vehicle endpoint is a default OMS write
  selectMode: boolean;
  onToggleSelectMode: () => void;
  canAutoAssign: boolean; // `oms.logistics` — what the auto-assign / assign-owner endpoints require
}

const BoardControls: React.FC<Props> = (p) => (
  <>
    {p.summary && (
      <Space wrap size={8} style={{ marginBottom: 12 }}>
        <Tag.CheckableTag
          checked={!p.attention}
          onChange={() => p.setAttention("")}
          style={{ border: "1px solid #eee", padding: "2px 10px" }}
        >
          All · {p.summary.total}
        </Tag.CheckableTag>
        {ATTENTION_ORDER.filter((b) => p.summary!.counts[b] > 0).map((b) => (
          <Tooltip key={b} title={ATTENTION_META[b].help}>
            <Tag.CheckableTag
              checked={p.attention === b}
              onChange={(c) => p.setAttention(c ? b : "")}
              style={{ border: "1px solid #eee", padding: "2px 10px" }}
            >
              {ATTENTION_META[b].label} · {p.summary!.counts[b]}
            </Tag.CheckableTag>
          </Tooltip>
        ))}
        {p.summary.sla_breached > 0 && (
          <Tooltip title="Past the leg's due time and not yet delivered. Filters alongside the buckets to the left, not instead of them.">
            <Tag.CheckableTag
              checked={p.sla === "breached"}
              onChange={(c) => p.setSla(c ? "breached" : "")}
              style={{
                border: "1px solid #ffccc7",
                padding: "2px 10px",
                color: p.sla === "breached" ? undefined : "#cf1322",
                background: p.sla === "breached" ? "#cf1322" : "#fff1f0",
              }}
            >
              SLA breached · {p.summary.sla_breached}
            </Tag.CheckableTag>
          </Tooltip>
        )}
      </Space>
    )}

    {/* Filters on the left, actions pinned right. */}
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Dispatch on / before</div>
        <DatePicker
          allowClear={false}
          format="DD MMM"
          value={p.date}
          onChange={(v) => v && p.setDate(v)}
          presets={[
            { label: "Today", value: dayjs() },
            { label: "Tomorrow", value: dayjs().add(1, "day") },
          ]}
        />
      </div>
      {p.setCity && (
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Delivery city</div>
          <Select
            allowClear
            placeholder="All cities"
            value={p.city}
            style={{ width: 150 }}
            options={CITY_OPTIONS}
            onChange={(v) => p.setCity?.(v ?? undefined)}
          />
        </div>
      )}
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Warehouse</div>
        <Select
          allowClear
          placeholder="All warehouses"
          value={p.warehouse}
          style={{ width: 190 }}
          onChange={(v) => p.setWarehouse(v ?? undefined)}
          options={p.warehouses.map((w) => ({
            value: w.id,
            label: w.city ? `${w.label} · ${w.city}` : w.label,
          }))}
        />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Carrier</div>
        <Select value={p.provider} options={PROVIDER_OPTIONS} style={{ width: 180 }} onChange={p.setProvider} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Owner</div>
        <Select
          placeholder="Assigned to…"
          allowClear
          showSearch
          style={{ width: 140 }}
          value={p.assignee || undefined}
          onChange={(v) => p.setAssignee(v ?? "")}
          onClear={() => p.setAssignee("")}
          options={[
            { value: UNASSIGNED_OWNER, label: "Unassigned (no owner)" },
            ...p.teamMembers.map((m) => ({ value: m.value, label: m.label })),
          ]}
          filterOption={(input, opt) =>
            String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Express only</div>
        <div style={{ height: 32, display: "flex", alignItems: "center" }}>
          <Switch checked={p.expressOnly} onChange={p.setExpressOnly} />
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Contains Transfer Orders</div>
        <div style={{ height: 32, display: "flex", alignItems: "center" }}>
          <Tooltip title="Only trucks whose allotment moves through a WH→WH transfer order — the intercity chain">
            <Switch checked={p.transferOnly} onChange={p.setTransferOnly} />
          </Tooltip>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Show cancelled</div>
        <div style={{ height: 32, display: "flex", alignItems: "center" }}>
          <Switch checked={p.showCancelled} onChange={p.setShowCancelled} />
        </div>
      </div>
      {p.setVendor && (
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Vendor</div>
          <Tooltip title="Narrows the tables below to one pickup point. The counts above stay board-wide.">
            <Select
              allowClear
              showSearch
              placeholder="All vendors"
              style={{ width: 200 }}
              value={p.vendor}
              onChange={(v) => p.setVendor?.(v ?? undefined)}
              options={(p.vendorOptions ?? []).map((v) => ({ value: v, label: v }))}
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Tooltip>
        </div>
      )}
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Group</div>
        <Select value={p.groupBy} options={GROUP_BY_OPTIONS} style={{ width: 190 }} onChange={p.setGroupBy} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Search</div>
        <Input.Search
          autoFocus={p.autoFocusSearch}
          placeholder="Carrier ref, ENQ or PO number…"
          allowClear
          style={{ width: 260 }}
          value={p.searchInput}
          onChange={(e) => p.setSearchInput(e.target.value)}
        />
      </div>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end", gap: 8 }}>
        {p.filtersApplied && <Button onClick={p.clearFilters}>Clear</Button>}
        <Tooltip title="Refresh now">
          <Button icon={<ReloadOutlined />} onClick={p.onRefresh} loading={p.loading} />
        </Tooltip>
        {p.canAutoAssign && (
          <Tooltip title="Tick bookings in the tables, then auto-assign their ops owner from the routing table">
            <Button
              type={p.selectMode ? "primary" : "default"}
              icon={<UsergroupAddOutlined />}
              onClick={p.onToggleSelectMode}
            >
              {p.selectMode ? "Done selecting" : "Select to auto-assign"}
            </Button>
          </Tooltip>
        )}
        {p.canAssignTrip && (
          <Button type="primary" icon={<CarOutlined />} onClick={p.onAssignTrip}>
            Assign trip vehicle
          </Button>
        )}
      </div>
    </div>
  </>
);

export default BoardControls;
